import TaskAltIcon from '@mui/icons-material/TaskAlt';
import {
    Box,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
} from '@mui/material';
import { motion } from 'framer-motion';
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { OurServices_IMG, Programs_IMG } from '../../Constants/ImageConstants';
import { COLORS } from '../../Constants/styles';

const programsContent = [
    {
        id: 'in-school-sports',
        title: 'In-School Sports & Physical Education Programs',
        subtitle: 'Curriculum-Aligned Sports & Physical Education from KG to Grade 12',
        description:
            'Our In-School Sports & Physical Education Program integrates structured sports learning into the school curriculum, delivering engaging multimedia-based lessons through images, videos, and interactive resources. Designed to align with educational standards, the program develops physical literacy, motor skills, teamwork, discipline, and confidence while fostering a lifelong appreciation for health, fitness, and active living. Through qualified coaches and age-appropriate activities, we create enjoyable learning experiences that support every child\'s holistic development.',
        image: Programs_IMG.kidsnadultsImage,
        sports: [
            'Curriculum-aligned Sports & PE for KG to Grade 12',
            'Multimedia learning (Images, Videos & Digital Resources)',
            'Qualified and Certified Sports Educators',
            'Age-Appropriate Skill Development',
            'Physical Literacy & Fitness Assessments',
            'Character Building through Sport',
            'Progress Tracking & Student Reports',
            'Safe, Inclusive, and Engaging Learning Environment',
        ],
    },
    {
        id: 'coach-education',
        title: 'Grassroots Sports Coach Education',
        subtitle: 'Strengthening the Sports Ecosystem from the Ground Up',
        description:
            'Our Coach Education Program equips coaches, physical education teachers, and sports professionals with the knowledge, practical skills, and confidence to deliver safe, engaging, and child-centered coaching. Through structured training, modern coaching methodologies, sports science fundamentals, and continuous professional development, we help educators create positive learning environments that inspire participation, improve performance, and nurture lifelong involvement in sport.',
        image: Programs_IMG.kidsnadults_2Image,
        sports: [
            'Foundation to Advanced Coach Education Programs',
            'Child-Centered & Age-Appropriate Coaching',
            'Modern Coaching Methodologies',
            'Sports Science Fundamentals',
            'Session Planning & Skill Development',
            'Safety, Ethics & Safeguarding Practices',
            'Practical Workshops & Assessments',
            'Continuous Professional Development (CPD)',
        ],
    },
    {
        id: 'recruitment-hr',
        title: 'Sports Professional Recruitment & HR Services',
        subtitle: 'Connecting Talent with Opportunity in the Sports Industry',
        description:
            'We specialize in recruiting, training, and deploying qualified sports professionals to schools, academies, high-performance centres, and sports organizations. From coaches and Physical Education teachers to sports scientists and performance specialists, we connect institutions with the right talent to build strong sports programs and achieve long-term excellence.',
        image: Programs_IMG.kidsnadults_3Image,
        sports: [
            'Sports Coach Recruitment & Placement',
            'Physical Education Teacher Recruitment',
            'Academy & School Sports Staffing',
            'High Performance Centre (HPC) Staffing',
            'Centre of Excellence Recruitment',
            'Sports Science Professionals',
            'Talent Screening & Verification',
            'Training, Deployment & HR Support',
        ],
    },
    {
        id: 'events-management',
        title: 'Sports Events & Tournament Management',
        subtitle: 'Seamless Planning and Execution of Sports Events',
        description:
            'We deliver professionally managed sports events and tournaments for schools, colleges, academies, corporate organizations, and communities. From concept to completion, our experienced team manages every aspect—including planning, scheduling, branding, logistics, officiating, technology, and event operations—to ensure a safe, engaging, and memorable sporting experience for participants and spectators alike.',
        image: Programs_IMG.kidsnadults_4Image,
        sports: [
            'School, College & Inter-School Tournaments',
            'Corporate & Community Sports Events',
            'Event Planning & Operations Management',
            'Tournament Fixtures, Scheduling & Results',
            'Technical Officials & Referee Management',
            'Branding, Marketing & Sponsorship Support',
            'Registration, Scoring & Event Technology',
            'Awards, Ceremonies & Post-Event Reporting',
        ],
    },
    {
        id: 'sports-science',
        title: 'Sports Science Consultation Services',
        subtitle: 'Applying Science to Enhance Performance and Well-being',
        description:
            'Our Sports Science Consultation Services help schools, academies, and sports organizations optimize athlete development through evidence-based practices. Our multidisciplinary team provides expert guidance in injury prevention, rehabilitation, performance enhancement, and long-term athlete development, ensuring safe, healthy, and sustainable participation in sport.',
        image: Programs_IMG.kidsnadults_5Image,
        sports: [
            'Sports Physiotherapy',
            'Sports Psychology',
            'Strength & Conditioning',
            'Sports Massage Therapy',
            'Sports Nutrition',
            'Performance Analysis & Biomechanics',
            'Sports Technology & Athlete Monitoring',
            'Injury Prevention & Return-to-Play Planning',
        ],
    },
    {
        id: 'infrastructure',
        title: 'Sports Infrastructure Consultation Services',
        subtitle: 'Building Safe, Functional, and Future-Ready Sports Facilities',
        description:
            'We provide end-to-end sports infrastructure consultation for schools, academies, universities, communities, and sports organizations. From planning and design to equipment selection and facility development, we help create safe, sustainable, and high-quality sports environments that encourage participation, performance, and long-term growth.',
        image: Programs_IMG.kidsnadults_6Image,
        sports: [
            'Sports Facility Planning & Design',
            'Indoor & Outdoor Sports Infrastructure',
            'Court & Field Layout Consultation',
            'Equipment Planning & Procurement',
            'Safety & Compliance Standards',
            'School & Academy Sports Facility Development',
            'High Performance Centre & Centre of Excellence Planning',
            'Facility Audit, Upgradation & Maintenance Consultation',
        ],
    },
    {
        id: 'youth-fitness',
        title: 'Youth Fitness & Conditioning Programs',
        subtitle: 'Age-Appropriate Fitness for Growing Bodies and Minds',
        description:
            'Our Youth Fitness & Conditioning Programs are designed to help children and young athletes develop a strong foundation of physical fitness through safe, progressive, and age-appropriate training. Focusing on movement quality, strength, agility, balance, coordination, flexibility, and endurance, these programs enhance sports performance while promoting lifelong health and active living.',
        image: Programs_IMG.kidsnadultsImage,
        sports: [
            'Age-Appropriate Fitness Training',
            'Strength & Conditioning Fundamentals',
            'Speed, Agility & Quickness (SAQ)',
            'Balance, Coordination & Mobility Development',
            'Flexibility & Injury Prevention',
            'Sport-Specific Conditioning',
            'Fitness Assessment & Progress Tracking',
            'Long-Term Athlete Development (LTAD)',
        ],
    },
    {
        id: 'sportswear-retail',
        title: 'Sportswear & Equipment Retail',
        subtitle: 'Reliable Sports Gear for Schools, Academies, and Teams',
        description:
            'We supply quality sportswear, team uniforms, training equipment, and accessories to support schools, academies, clubs, and sports organizations. Our solutions combine durability, performance, and value, ensuring athletes and institutions have the right equipment for training, competition, and everyday physical education.',
        image: Programs_IMG.kidsnadults_2Image,
        sports: [
            'School Sports Uniforms & Team Kits',
            'Training Equipment & Accessories',
            'Sports Balls, Nets & Practice Gear',
            'PE Equipment for Schools',
            'Custom Team Jerseys & Apparel',
            'Tournament & Event Equipment Supply',
            'Equipment Procurement & Bulk Orders',
            'Trusted Brands & Quality Assurance',
        ],
    },
    {
        id: 'branding-sponsorship',
        title: 'Sports Branding & Sponsorship Solutions',
        subtitle: 'Building Strong Sports Brands and Sustainable Partnerships',
        description:
            'We help schools, academies, sports organizations, and events build impactful sports brands while connecting them with strategic sponsorship opportunities. From brand identity and event promotion to partnership development and marketing support, we enhance visibility, engagement, and long-term sustainability across the grassroots sport\'s ecosystem.',
        image: Programs_IMG.kidsnadults_3Image,
        sports: [
            'Sports Brand Strategy & Identity',
            'School & Academy Sports Branding',
            'Event Branding & Promotion',
            'Sponsorship Strategy & Acquisition',
            'Corporate Partnership Development',
            'Marketing & Digital Campaigns',
            'Merchandise & Brand Collateral',
            'Community Engagement & Brand Activation',
        ],
    },
];

const ProgramSectionBlock = ({ data, reverse, index }: any) => (
    <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0 }}
        transition={{ duration: 0.4, }}
    >
        <Box maxWidth="xl" mx="auto" px={{ xs: 2, md: 1 }} py={{ xs: 2, md: 2 }}
            sx={{
                display: 'flex',
                flexDirection: {
                    xs: 'column',
                    md: reverse ? 'row-reverse' : 'row',
                },
                justifyContent: 'center',
                alignItems: 'stretch',
                gap: 4,
                mt: index !== 0 ? 6 : 0,
            }}
        >
            {/* Image */}
            <Box
                component="img"
                src={data.image}
                alt={data.title}
                sx={{
                    width: { xs: '100%', md: '45%' },
                    borderRadius: 3,
                    objectFit: 'cover',
                    height: { xs: 300, md: 'auto' },
                    alignSelf: { md: 'stretch' },
                }}
            />

            {/* Content */}
            <Box
                sx={{
                    backgroundColor: COLORS.white,
                    p: { xs: 3, md: 5 },
                    flex: 1,
                    borderRadius: 3,
                    boxShadow: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                }}
            >
                <Typography variant="h5" fontWeight="bold" gutterBottom>{data.subtitle}</Typography>

                <Typography
                    variant="h3"
                    gutterBottom
                    sx={{ color: COLORS.pureBlack }}
                >
                    {data.title}
                </Typography>

                <Typography variant="body2" sx={{ mb: 2, color: COLORS.accentBlackTransparent }}>
                    {data.description}
                </Typography>

                <Box sx={{ pt: { xs: 3, md: 4 }, pb: 0 }}>
                    <Divider sx={{ mb: 4 }} />

                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, textTransform: 'uppercase', color: COLORS.accentDarkGreen }}>
                        What We Offer
                    </Typography>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(2, 1fr)',
                                md: 'repeat(2, 1fr)',
                            },
                            gap: 2,
                        }}
                    >
                        {data.sports.map((sport: string) => (
                            <List key={sport} dense disablePadding>
                                <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
                                    <ListItemIcon
                                        sx={{
                                            minWidth: '32px',
                                            mt: '2px',
                                            color: COLORS.accentDarkGreen,
                                            stroke: '#000',
                                            strokeWidth: 0.5,
                                            fill: 'none',
                                            '& path': {
                                                stroke: 'black',
                                                strokeWidth: 0.5,
                                            },
                                        }}
                                    >
                                        <TaskAltIcon />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={sport}
                                        slotProps={{
                                            primary: {
                                                fontWeight: 'bold',
                                                textTransform: 'uppercase',
                                                fontFamily: "'Big Shoulders Text', sans-serif",
                                                color: COLORS.pureBlack,
                                            }
                                        }}
                                    />
                                </ListItem>
                            </List>
                        ))}
                    </Box>

                    <Divider sx={{ mt: 4, mb: 0 }} />
                </Box>
            </Box>
        </Box>
    </motion.div>
);

const ProgramsForEveryone: React.FC = () => {
    const location = useLocation();

    useEffect(() => {
        const hash = location.hash.slice(1);
        if (!hash) return;
        const el = document.getElementById(hash);
        if (!el) return;
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }, [location.hash]);

    return (
        <>
            <Box
                sx={{
                    px: { xs: 2, md: 6 },
                    py: { xs: 6, md: 8 },
                    backgroundColor: COLORS.greyBG,
                }}
            >
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.6 }}
                >
                    <Typography
                        variant="h3"
                        align="center"
                        fontWeight="bold"
                        sx={{ textTransform: 'uppercase', mb: 6, color: COLORS.pureBlack }}
                    >
                        Our Services
                    </Typography>
                </motion.div>

                <Box>
                    {programsContent.map((item, index) => (
                        <Box key={item.id} id={item.id}>
                            <ProgramSectionBlock
                                data={item}
                                reverse={index % 2 !== 0}
                                index={index}
                            />
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* banner section */}
            <Box
                sx={{
                    position: 'relative',
                    backgroundImage: `url(${OurServices_IMG.bottom_banner})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    minHeight: { xs: '44vh', md: '60vh' },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    overflow: 'hidden',
                    px: 2,
                }}
            >
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                >
                    <Typography variant="h3" fontWeight="bold" sx={{ mb: 3, color: COLORS.white, textTransform: 'capitalize' }}>
                    </Typography>
                </motion.div>
            </Box>
        </>
    );
};

export default ProgramsForEveryone;
